import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import MDButton from 'components/MDButton';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import { addFoulWord, getFoulWords } from '../../../redux/slices/foulWordsSlice';
import { Cancel as CancelIcon } from "@mui/icons-material";
import { orange } from "@mui/material/colors";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import MDTypography from 'components/MDTypography';

const AddFoulWordsSection = () => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsLoading(true);
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const words = XLSX.utils.sheet_to_json(sheet, { header: 1 }).flat().filter(Boolean);

      const payload = {
        foulWordList: words.map((word) => word.toString().trim()),
      };

      const res = await dispatch(addFoulWord(payload));
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(getFoulWords())
        Swal.fire({
          icon: "success",
          title: "Foul words uploaded successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
        setOpen(false);
      } else {
        throw new Error(res.payload || "Failed to upload foul words.");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed", "error");
    } finally {
      setIsLoading(false);
      setSelectedFile(null);
    }
  };

  return (
    <>
      <MDButton
        variant="contained"
        size="small"
        sx={{
          color: "black",
          py: "13px",
          width: { xs: "100%", md: "auto" },
        }}
        onClick={() => setOpen(true)}
      >
        Add/Update List
      </MDButton>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 600,
            pr: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Upload Foul Words
          <IconButton onClick={() => setOpen(false)} size="small">
            <CancelIcon sx={{ color: orange[500] }} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box
            component="label"
            sx={{
              border: "1.5px dashed #ccc",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              backgroundColor: "#fafafa",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              transition: "0.3s",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#ff980080",
              },
            }}
          >
            <input hidden type="file" accept=".xlsx" onChange={handleFileChange} />

            <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "#757575" }} />

            <Typography variant="subtitle1" fontWeight={500} mt={2} color="text.primary">
              Click to upload Excel file
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={0.5}>
              .xlsx format only – one word per line
            </Typography>
          </Box>

          {selectedFile && (
            <Typography
              mt={2}
              variant="body2"
              fontWeight={500}
              color="text.primary"
              textAlign="center"
            >
              Selected: {selectedFile.name}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>

          <MDButton onClick={handleUpload} variant="gradient" color="light" fullWidth
          >
            <MDTypography
              style={{ fontSize: "14.5px", fontWeight: "bold" }}
              color="white"
            >
              {isLoading ? "Uploading..." : "Upload"}
            </MDTypography>
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddFoulWordsSection;
